import { memo, useCallback, useMemo } from 'react';
import {
  Award,
  ChevronRight,
  Edit,
  Mail,
  MessageCircle,
  Phone,
  Send,
  ShieldAlert,
  ShieldCheck,
  ShoppingBag,
  Store,
  Wallet,
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  currency,
  currencySymbol,
  formatDate,
  toFiniteNumber,
  whatsappLink,
  type CurrencyCode,
} from '@/shared/lib/format';
import { frontBase, isCardInstalled, walletPlatform } from '@/shared/lib/passbook-utils';
import { formatUniqueIdForDisplay } from '@/shared/lib/unique-id-lookup';
import { canResendCard, formatCooldownRemaining } from '@/features/leads/model/card-resend';
import type { Lead, PassbookTier } from '@/shared/types';
import type { BadgeProps } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { DropdownMenuItem, DropdownMenuSeparator } from '@/shared/ui/dropdown-menu';
import { EntityListCard, type EntityListCardBadge } from '@/shared/components/entity-list-card';
import { ListCardActions } from '@/shared/components/list-card-actions';
import { LeadAvatar } from '@/shared/components/lead-avatar';
import { Badge } from '@/shared/ui/badge';
import { Tooltip } from '@/shared/ui/tooltip';
import { resendCard } from '../api/use-leads';

/** Link de convite do cartão — legado usa /meucartao/{hashRegenerate}. */
export function inviteCardUrl(hashRegenerate: string): string {
  return `${frontBase()}/meucartao/${hashRegenerate}`;
}

export function inviteWhatsappMessage(lead: Lead): string | undefined {
  const hash = lead.hashRegenerate?.trim();
  if (!hash) return undefined;
  return `Olá, ${lead.profile?.name ?? ''}! Segue o link do seu cartão digital: ${inviteCardUrl(hash)}`;
}

interface LeadCardProps {
  lead: Lead;
  selected: boolean;
  onToggle: (id: string) => void;
  onEdit: (lead: Lead) => void;
  idPassbook: string;
  labelQtde: string;
  isCashback: boolean;
  currencyCode?: CurrencyCode;
  canSeeEmail: boolean;
  uniqueIdLabel?: string;
  hasMultiStore: boolean;
  resolveLeadStores?: (lead: Lead) => string[];
  /** Presente só quando `passbook.hasTiers` — habilita o badge de nível. */
  tiers?: PassbookTier[];
  index: number;
  hideSelection?: boolean;
  /** Com ≥1 lead selecionado, clique no card alterna seleção em vez de abrir detalhe. */
  selectionMode?: boolean;
  /**
   * Modo “escolher cliente” (ex.: Operador) — clique chama onPick em vez de abrir detalhe.
   * Também esconde checkbox e o menu de ações da lista.
   */
  onPick?: (lead: Lead) => void;
  /** Presente na página de Auditoria — adiciona Bloquear/Desbloquear ao menu do card. */
  onToggleBlocked?: (lead: Lead) => void;
}

function cardBadgeVariant(platform: string, installed: boolean): BadgeProps['variant'] {
  if (!installed) return 'secondary';
  if (platform === 'iOS') return 'ios';
  if (platform === 'Android' || platform === 'Google Wallet') return 'android';
  return 'success';
}

export const LeadCard = memo(function LeadCard({
  lead,
  selected,
  onToggle,
  onEdit,
  idPassbook,
  labelQtde,
  isCashback,
  currencyCode = 'BRL',
  canSeeEmail,
  uniqueIdLabel = 'CPF',
  hasMultiStore,
  resolveLeadStores,
  tiers,
  index,
  hideSelection = false,
  selectionMode = false,
  onPick,
  onToggleBlocked,
}: LeadCardProps) {
  const navigate = useNavigate();
  const pickMode = Boolean(onPick);
  const installed = isCardInstalled(lead);
  const platform = walletPlatform(lead);
  const points = isCashback
    ? currency(lead.total_points ?? 0, currencyCode)
    : String(toFiniteNumber(lead.total_points ?? 0));
  const cardMessage = inviteWhatsappMessage(lead);

  const storeNames = useMemo(() => {
    if (!hasMultiStore || !resolveLeadStores) return [];
    return resolveLeadStores(lead);
  }, [hasMultiStore, resolveLeadStores, lead]);

  const handleToggle = useCallback(() => onToggle(lead._id), [onToggle, lead._id]);

  const openDetail = useCallback(() => {
    navigate(`/loja/${idPassbook}/leads/${lead._id}`, { state: { lead } });
  }, [navigate, idPassbook, lead]);

  const handleResendEmail = useCallback(async () => {
    if (!lead.serialNumber || !lead.email) return;
    const eligibility = canResendCard(lead);
    if (!eligibility.allowed) {
      toast.error(`Aguarde ${formatCooldownRemaining(eligibility.remainingMs)} para reenviar.`);
      return;
    }
    try {
      await resendCard(idPassbook, lead.serialNumber, lead.email);
      toast.success('Cartão reenviado por e-mail.');
    } catch {
      toast.error('Falha ao reenviar.');
    }
  }, [idPassbook, lead]);

  const canInvite = Boolean(lead.hashRegenerate && (lead.email || lead.phone));
  const storeLabel =
    storeNames.length === 0
      ? '—'
      : storeNames.length === 1
        ? storeNames[0]!
        : `${storeNames.length} lojas`;
  const cardLabel = installed ? platform : 'Sem cartão';
  const pointsLabel = isCashback ? points : `${points}`;

  const tierBadge = useMemo<EntityListCardBadge | null>(() => {
    if (!lead.tier) return null;
    const color = tiers?.find((t) => t.level === lead.tier!.level)?.color;
    const isOverride = Boolean(lead.tierOverride?.level);
    return {
      key: 'tier',
      label: `${lead.tier.label}${isOverride ? ' *' : ''}`,
      icon: Award,
      variant: 'outline',
      size: 'sm',
      style: color ? { borderColor: color, color } : undefined,
      copyLines: [
        { label: 'Nível', value: lead.tier.label },
        ...(isOverride ? [{ label: 'Ajustado manualmente', value: lead.tierOverride?.reason || 'Sim' }] : []),
      ],
    };
  }, [lead.tier, lead.tierOverride, tiers]);

  const badges = useMemo<EntityListCardBadge[]>(
    () => [
      {
        key: 'contact',
        label: 'Contato',
        icon: Phone,
        variant: 'info',
        disabled: !lead.phone && !(canSeeEmail && lead.email),
        // Desktop: badge Contato. Mobile: meta com telefone/e-mail.
        className: 'hidden sm:flex',
        copyLines: [
          ...(lead.phone ? [{ label: 'Telefone', value: lead.phone }] : []),
          ...(canSeeEmail && lead.email ? [{ label: 'E-mail', value: lead.email }] : []),
        ],
      },
      ...(hasMultiStore
        ? [
            {
              key: 'stores',
              label: storeLabel,
              icon: Store,
              variant: 'accent' as const,
              disabled: storeNames.length === 0,
              copyLines: storeNames.map((name) => ({ value: name })),
            },
          ]
        : []),
      {
        key: 'card',
        label: cardLabel,
        icon: ShoppingBag,
        variant: cardBadgeVariant(platform, installed),
        wide: !installed,
        copyLines: [
          { label: 'Plataforma', value: platform },
          { label: 'Status', value: installed ? 'Cartão instalado' : 'Cartão não instalado' },
          ...(lead.lastCardResendAt
            ? [{ label: 'Reenviado', value: formatDate(lead.lastCardResendAt) }]
            : []),
        ],
      },
      ...(tierBadge ? [tierBadge] : []),
      {
        key: 'points',
        label: pointsLabel,
        icon: Wallet,
        variant: 'default',
        size: 'sm',
        copyLines: [
          { label: labelQtde, value: points },
          { label: 'Tipo', value: isCashback ? `Cashback (${currencySymbol(currencyCode)})` : 'Pontos' },
        ],
      },
      ...(lead.status === 'blocked'
        ? [
            {
              key: 'blocked',
              label: 'Bloqueado',
              icon: ShieldAlert,
              variant: 'destructive' as const,
              copyLines: [{ label: 'Status', value: 'Cliente bloqueado' }],
            },
          ]
        : []),
    ],
    [
      lead,
      canSeeEmail,
      hasMultiStore,
      storeLabel,
      storeNames,
      cardLabel,
      platform,
      installed,
      pointsLabel,
      points,
      labelQtde,
      isCashback,
      currencyCode,
      tierBadge,
    ],
  );

  const handleEdit = useCallback(() => onEdit(lead), [onEdit, lead]);

  const handlePick = useCallback(() => {
    onPick?.(lead);
  }, [onPick, lead]);

  const handleToggleBlocked = useCallback(() => {
    onToggleBlocked?.(lead);
  }, [onToggleBlocked, lead]);

  const isBlocked = lead.status === 'blocked';

  const onCardClick = useCallback(() => {
    if (pickMode) {
      handlePick();
      return;
    }
    if (hideSelection || !selectionMode) openDetail();
    else handleToggle();
  }, [pickMode, handlePick, hideSelection, selectionMode, openDetail, handleToggle]);

  const phone = lead.phone?.trim() || '';
  const email = canSeeEmail ? lead.email?.trim() || '' : '';

  const subtitle = useMemo(() => {
    if (pickMode) {
      return (
        <p className="text-xs text-muted-foreground sm:text-sm [overflow-wrap:anywhere]">
          {lead.profile?.uniqueId ?? '—'}
        </p>
      );
    }

    const created = lead.createdAt ? formatDate(lead.createdAt) : null;
    const updated = lead.updated ? formatDate(lead.updated) : null;
    const uniqueId = formatUniqueIdForDisplay(lead.profile?.uniqueId, uniqueIdLabel);
    if (!created && !updated && !uniqueId) return null;
    return (
      <div className="mt-1 flex min-w-0 flex-wrap items-center gap-1.5">
        {updated ? (
          <Tooltip content={`Atualizado em ${updated}`} side="top">
            <span className="inline-flex max-w-[9.5rem]">
              <Badge
                variant="outline"
                className="h-5 max-w-full truncate whitespace-nowrap px-1.5 text-[10px] font-normal text-muted-foreground"
              >
                Atualizado {updated}
              </Badge>
            </span>
          </Tooltip>
        ) : null}
        {created ? (
          <Tooltip content={`Cadastro em ${created}`} side="top">
            <span className="inline-flex max-w-[8.5rem]">
              <Badge
                variant="secondary"
                className="h-5 max-w-full truncate whitespace-nowrap border-transparent px-1.5 text-[10px] font-normal"
              >
                Cadastro {created}
              </Badge>
            </span>
          </Tooltip>
        ) : null}
        {uniqueId ? (
          <Tooltip content={`${uniqueIdLabel} ${uniqueId}`} side="top">
            <span className="inline-flex max-w-[12rem]">
              <Badge
                variant="secondary"
                className="h-5 max-w-full truncate whitespace-nowrap border-transparent px-1.5 text-[10px] font-normal"
              >
                {uniqueIdLabel} {uniqueId}
              </Badge>
            </span>
          </Tooltip>
        ) : null}
      </div>
    );
  }, [pickMode, lead.profile?.uniqueId, lead.createdAt, lead.updated, uniqueIdLabel]);

  /** Contato legível — só mobile (desktop mantém badge Contato). */
  const contactMeta = useMemo(() => {
    if (pickMode) return null;

    if (!phone && !email) {
      return (
        <div className="sm:hidden">
          <p className="text-[11px] text-muted-foreground/70">Sem telefone ou e-mail</p>
        </div>
      );
    }

    return (
      <div
        className="flex min-w-0 flex-col gap-0.5 sm:hidden"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        {phone ? (
          <a
            href={whatsappLink(phone, true)}
            target="_blank"
            rel="noreferrer"
            className="inline-flex max-w-full items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
            aria-label={`WhatsApp ${phone}`}
          >
            <Phone className="size-3 shrink-0 opacity-80" />
            <span className="truncate">{phone}</span>
          </a>
        ) : null}
        {email ? (
          <a
            href={`mailto:${email}`}
            className="inline-flex max-w-full items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
            aria-label={`E-mail ${email}`}
          >
            <Mail className="size-3 shrink-0 opacity-80" />
            <span className="truncate">{email}</span>
          </a>
        ) : null}
      </div>
    );
  }, [pickMode, phone, email]);

  return (
    <EntityListCard
      selected={selected}
      onToggle={handleToggle}
      onCardClick={onCardClick}
      index={index}
      ariaLabel={
        pickMode
          ? `Usar cliente ${lead.profile?.name ?? 'lead'}`
          : `Selecionar ${lead.profile?.name ?? 'lead'}`
      }
      title={lead.profile?.name || 'Sem nome'}
      subtitle={subtitle}
      meta={contactMeta}
      thumbnail={<LeadAvatar lead={lead} className="size-11" />}
      badges={badges}
      badgesBelow={pickMode}
      selectable={!hideSelection && !pickMode}
      actions={
        pickMode ? (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="h-9 gap-1 rounded-xl px-3"
            onClick={(e) => {
              e.stopPropagation();
              handlePick();
            }}
          >
            Usar
            <ChevronRight className="size-4" />
          </Button>
        ) : (
          <ListCardActions
            menuChildren={
              <>
                <DropdownMenuItem onSelect={handleEdit}>
                  <Edit />
                  Editar cadastro
                </DropdownMenuItem>
                {lead.phone || canInvite ? <DropdownMenuSeparator /> : null}
                {lead.phone ? (
                  <DropdownMenuItem asChild>
                    <a href={whatsappLink(lead.phone!, true)} target="_blank" rel="noreferrer">
                      <MessageCircle />
                      WhatsApp
                    </a>
                  </DropdownMenuItem>
                ) : null}
                {canInvite && lead.serialNumber && lead.email ? (
                  <DropdownMenuItem onSelect={handleResendEmail}>
                    <Mail />
                    Convite por e-mail
                  </DropdownMenuItem>
                ) : null}
                {canInvite && lead.phone && cardMessage ? (
                  <DropdownMenuItem asChild>
                    <a
                      href={whatsappLink(lead.phone!, true, cardMessage)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Send />
                      Convite por WhatsApp
                    </a>
                  </DropdownMenuItem>
                ) : null}
                {onToggleBlocked ? (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onSelect={handleToggleBlocked}
                      className={isBlocked ? undefined : 'text-destructive'}
                    >
                      {isBlocked ? <ShieldCheck /> : <ShieldAlert />}
                      {isBlocked ? 'Desbloquear' : 'Bloquear'}
                    </DropdownMenuItem>
                  </>
                ) : null}
              </>
            }
          />
        )
      }
    />
  );
});
